import { elasticsearchService } from './elasticsearchService';
import {
  loadClearanceConfig,
  type ClearanceRunnerConfig,
} from './eventClearanceRunner';

export interface CommandSearchInput {
  players: { uuid: string; name: string | null }[];
  server: string;
  start: number;
  end: number;
  commands: string;
}

export interface CommandSearchHit {
  _id: string;
  _index: string;
  _source: Record<string, unknown>;
}

interface CommandSearchResponse {
  timed_out?: boolean;
  _shards?: { failed?: number };
  hits?: {
    total?: { value: number; relation: string };
    hits?: CommandSearchHit[];
  };
  aggregations?: {
    players?: { buckets?: Record<string, { doc_count: number }> };
  };
}

export const canonicalPlayerUuid = (uuid: string) =>
  uuid.replace(/-/g, '').toLowerCase();
const uuidVariants = (uuid: string) => {
  const bare = canonicalPlayerUuid(uuid);
  return [
    bare,
    `${bare.slice(0, 8)}-${bare.slice(8, 12)}-${bare.slice(12, 16)}-${bare.slice(16, 20)}-${bare.slice(20)}`,
  ];
};

export function buildCommandSearchQuery(
  input: CommandSearchInput,
  config: ClearanceRunnerConfig,
): Record<string, unknown> {
  if (!input.players.length)
    throw new Error('Choose at least one player or group.');
  if (input.players.length > 100)
    throw new Error('Search up to 100 players at a time.');
  if (
    input.players.some(
      (p) => !/^[0-9a-f]{32}$/.test(canonicalPlayerUuid(p.uuid)),
    )
  )
    throw new Error('One of the players has an invalid UUID.');
  if (!input.server.trim()) throw new Error('Choose an event server.');
  if (
    !Number.isFinite(input.start) ||
    !Number.isFinite(input.end) ||
    input.end <= input.start
  )
    throw new Error('The end time must be after the start time.');
  const commands = Array.from(
    new Set(
      input.commands
        .split(/[\s,]+/)
        .map((s) => s.trim().replace(/^\//, ''))
        .filter(Boolean),
    ),
  );
  if (commands.some((c) => !/^[\w:.-]+$/.test(c)) || commands.length > 50)
    throw new Error(
      'Enter up to 50 command names, such as /fly or /give, separated by commas.',
    );
  const { mapping } = config;
  const exact = (field: string) =>
    config.stripKeywordSuffixes ? field : `${field}.keyword`;
  const filter: unknown[] = [
    {
      terms: {
        [mapping.player_uuid]: Array.from(
          new Set(input.players.flatMap((p) => uuidVariants(p.uuid))),
        ),
      },
    },
    { term: { [exact(mapping.server)]: input.server.trim() } },
    {
      range: {
        [mapping.timestamp]: {
          gte: input.start,
          lte: input.end,
          format: 'epoch_millis',
        },
      },
    },
    { exists: { field: mapping.command } },
  ];
  if (commands.length) {
    filter.push({
      bool: {
        minimum_should_match: 1,
        should: commands.flatMap((command) =>
          [command, `/${command}`].flatMap((value) => [
            { term: { [exact(mapping.command)]: value } },
            { prefix: { [exact(mapping.command)]: `${value} ` } },
          ]),
        ),
      },
    });
  }
  // Named filters give exact counts for every requested player, including zero.
  const players = Object.fromEntries(
    input.players.map((p) => [
      canonicalPlayerUuid(p.uuid),
      { terms: { [mapping.player_uuid]: uuidVariants(p.uuid) } },
    ]),
  );
  return {
    size: 100,
    track_total_hits: true,
    query: { bool: { filter } },
    sort: [{ [mapping.timestamp]: 'desc' }],
    _source: Array.from(
      new Set([
        mapping.player_uuid,
        mapping.command,
        mapping.timestamp,
        mapping.server,
        'args',
        'fromPlayerName',
      ]),
    ),
    aggs: { players: { filters: { filters: players } } },
  };
}

export async function searchEventCommands(input: CommandSearchInput) {
  const config = loadClearanceConfig();
  const response =
    await elasticsearchService.searchIndex<CommandSearchResponse>(
      config.index,
      buildCommandSearchQuery(input, config),
    );
  if (response.timed_out || (response._shards?.failed ?? 0) > 0)
    throw new Error(
      'The search was incomplete. Try again or use a shorter time range.',
    );
  const total = response.hits?.total;
  const hits = response.hits?.hits;
  const buckets = response.aggregations?.players?.buckets;
  if (
    !total ||
    total.relation !== 'eq' ||
    !Number.isFinite(total.value) ||
    !Array.isArray(hits) ||
    !buckets ||
    input.players.some(
      (p) => !Number.isFinite(buckets[canonicalPlayerUuid(p.uuid)]?.doc_count),
    )
  ) {
    throw new Error(
      'The command service did not return a complete result. Try again.',
    );
  }
  return {
    input,
    total: total.value,
    hits,
    mapping: config.mapping,
    players: input.players.map((p) => ({
      ...p,
      count: buckets[canonicalPlayerUuid(p.uuid)].doc_count,
    })),
  };
}

export type CommandSearchResult = Awaited<
  ReturnType<typeof searchEventCommands>
>;
